import React from 'react';
import { NavItem, NavSection } from './BaseLayout';
import { PERMISSIONS } from '@/constants/permissions';

export interface LayoutConfig {
  sidebarTitle: string;
  headerSubtitle: string;
  rootPath: string;
  navItems?: NavItem[]; // Legacy
  navSections?: NavSection[]; // New sectioned navigation
}

/**
 * Mutable layout registry.
 *
 * Boilerplate entries (admin, organization, account) are defined here.
 * Product-specific entries (e.g. roros) must be registered via
 * `registerLayoutConfig()` — they must NOT be hardcoded in this file.
 *
 * Rule #0: this boilerplate file must contain zero product-specific references.
 */
export const layoutConfigs: Record<string, LayoutConfig> = {
  admin: {
    sidebarTitle: "Global Settings",
    headerSubtitle: "Global Settings",
    rootPath: "/admin",
    navItems: [
      {
        label: 'Organizations',
        href: '/admin/orgs',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        )
      },
      {
        label: 'Users',
        href: '/admin/users',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        )
      },
      {
        label: 'LLM Usage',
        href: '/admin/llm/usage',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3v18M6 8v13M16 13v8M21 6v15" />
          </svg>
        )
      },
      {
        label: 'LLM Logs',
        href: '/admin/llm/logs',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10M4 18h8" />
          </svg>
        )
      },
      {
        label: 'Jobs',
        href: '/admin/jobs',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
          </svg>
        )
      },
      {
        label: 'Jobs List',
        href: '/admin/jobs/list',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        )
      },
      {
        label: 'Scheduled Jobs',
        href: '/admin/scheduled-jobs',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      {
        label: 'Translations',
        href: '/admin/translations',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
          </svg>
        )
      },
      {
        label: 'Integrations',
        href: '/admin/settings',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )
      },
      {
        label: 'Design System',
        href: '/admin/design-system',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h4" />
          </svg>
        )
      },
      {
        label: 'Database',
        href: 'https://rocket-routine-os-boilerplate-saas.crhq.ai/pgweb/',
        external: true,
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
          </svg>
        )
      },
      {
        label: 'Documentation',
        href: '/admin/documentation',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        )
      }
    ]
  },

  organization: {
    sidebarTitle: "Organization",
    headerSubtitle: "Organization Settings",
    rootPath: "/organization",
    navSections: [
      {
        // Main navigation (no title, appears first)
        items: [
          {
            label: 'Overview',
            href: '/organization',
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            )
          },
          {
            label: 'Members & Invites',
            href: '/organization/members',
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            ),
            requiredPermissions: [PERMISSIONS.VIEW_USERS, PERMISSIONS.MANAGE_USERS]
          },
          // Attachments and Chat nav entries hidden — see
          // docs/plans/archived/2026-04-15-hide-attachments-chat-notifications.md
          // Company Knowledge and Organization (About) nav entries removed
          // — replaced by ROROS overarching content (/roros/company-profile)
        ]
      },
      {
        // Administration section (only visible to users with MANAGE_ROLES or other admin permissions)
        title: "Administration",
        requiredPermissions: [PERMISSIONS.MANAGE_ROLES, PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS], // Section shows if user has ANY of these
        items: [
          {
            label: 'Roles & Permissions',
            href: '/organization/settings/roles',
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            ),
            requiredPermissions: [PERMISSIONS.MANAGE_ROLES]
          },
          {
            label: 'Translations',
            href: '/organization/translations',
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            ),
            requiredPermissions: [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]
          },
          {
            label: 'Design System',
            href: '/organization/theme',
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            ),
            requiredPermissions: [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]
          },
          {
            label: 'AI & LLM',
            href: '/organization/ai-settings',
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            ),
            requiredPermissions: [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]
          },
          // "Notifications" nav entry hidden — pointed at /organization/settings
          // (GeneralSettings), label was misleading. See
          // docs/plans/archived/2026-04-15-hide-attachments-chat-notifications.md
        ]
      }
    ]
  },

  account: {
    sidebarTitle: "Your Account",
    headerSubtitle: "Account Settings",
    rootPath: "/settings/account",
    navItems: [
      {
        label: 'Profile',
        href: '/settings/account/profile',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A7 7 0 0112 15a7 7 0 016.879 2.804M15 11a3 3 0 10-6 0 3 3 0 006 0z" />
          </svg>
        )
      },
      {
        label: 'Security',
        href: '/settings/account/security',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c.5304 0 1.0391-.2107 1.4142-.5858C13.7893 10.0391 14 9.5304 14 9V7a4 4 0 10-8 0v2c0 .5304.2107 1.0391.5858 1.4142C6.9609 10.7893 7.4696 11 8 11m4 0h-4m0 0v2a4 4 0 008 0v-2" />
          </svg>
        )
      },
      {
        label: 'Sessions',
        href: '/settings/account/sessions',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-2 .5L7.75 17M9 13h6m4 0a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h14z" />
          </svg>
        )
      },
      {
        label: 'Data Export',
        href: '/settings/account/export',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
        )
      },
      {
        label: 'Danger Zone',
        href: '/settings/account/danger',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z" />
          </svg>
        )
      }
    ]
  },
};

/**
 * Register a layout config with the registry.
 *
 * Call this at module load time (top-level side effect) so the config is
 * available before any React component renders. Registering the same key
 * twice replaces the previous entry (idempotent for hot-reload safety).
 *
 * @example
 * // src/routes/roros/layout.config.ts
 * import { registerLayoutConfig } from '@/components/Layout/layoutConfigs';
 * registerLayoutConfig('roros', rorosLayoutConfig);
 */
export function registerLayoutConfig(key: string, config: LayoutConfig): void {
  layoutConfigs[key] = config;
}
